import { useEffect, useState } from 'react';
import { SlideOver } from '../common/SlideOver';
import api from '../../lib/api';
import type { CockpitRepRow, CockpitRepFundraiserDetail } from '../../types';
import { formatDollarsCompact } from '../../lib/snapshotMetrics';
import { YoyPill } from './YoyPill';
import { FundraiserTrendBars } from './FundraiserTrendBars';

function lastSeenLabel(iso: string | null): string {
  if (!iso) return 'Never';
  const days = Math.floor((Date.now() - new Date(iso).getTime()) / 86_400_000);
  if (days <= 0) return 'Today';
  if (days === 1) return 'Yesterday';
  return `${days}d ago`;
}

const STATS: Array<{ label: string; key: keyof CockpitRepRow }> = [
  { label: 'Activities', key: 'activitiesTotal' },
  { label: 'Onsite', key: 'onsite' },
  { label: 'Conversions', key: 'conversions' },
  { label: 'Overdue', key: 'overdue' },
  { label: 'People', key: 'peopleOwned' },
  { label: 'Accounts', key: 'namedAccountsOwned' },
];

export function RepFundraiserPanel({ rep, rank, onClose }: { rep: CockpitRepRow | null; rank?: number; onClose: () => void }) {
  const [data, setData] = useState<CockpitRepFundraiserDetail | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!rep) { setData(null); setError(null); setLoading(false); return; }
    let cancelled = false;
    setLoading(true); setError(null); setData(null);
    api.get(`/api/dashboard/cockpit/rep/${rep.userId}/fundraising`)
      .then((r) => { if (!cancelled) setData(r.data as CockpitRepFundraiserDetail); })
      .catch(() => { if (!cancelled) setError('Failed to load rep detail'); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [rep?.userId]);

  const title = rep ? (
    <span className="flex items-baseline gap-2 min-w-0">
      <span className="truncate">{rep.displayName || rep.userId}</span>
      {rank !== undefined ? <span className="text-sm font-medium text-gray-400 dark:text-gray-500 flex-shrink-0">Rank #{rank}</span> : null}
    </span>
  ) : '';

  return (
    <SlideOver isOpen={!!rep} onClose={onClose} title={title}>
      {rep && (
        <div className="space-y-4">
          {/* Headline */}
          <div>
            <div className="text-3xl font-bold text-gray-900 dark:text-gray-100 tabular-nums">{formatDollarsCompact(rep.raisedThisSyCents)}</div>
            <div className="mt-1 text-sm flex items-center gap-2">
              <YoyPill pct={rep.fundraiserYoyPct} />
              <span className="text-gray-400 dark:text-gray-500">vs {formatDollarsCompact(rep.raisedPriorSyCents)} last year</span>
            </div>
          </div>

          {/* 4-year trend */}
          {data && data.trend.length > 0 && (
            <div className="card overflow-hidden">
              <div className="px-4 pt-3 text-[11px] uppercase tracking-wide text-gray-400 dark:text-gray-500 font-semibold">4-year trend</div>
              <FundraiserTrendBars trend={data.trend} />
            </div>
          )}

          {/* Activity strip (from the row) */}
          <div className="grid grid-cols-3 gap-px bg-gray-100 dark:bg-gray-700/60 rounded-lg overflow-hidden text-center">
            {STATS.map((s) => (
              <div key={s.label} className="bg-white dark:bg-gray-800 px-2 py-2">
                <div className="text-lg font-bold text-gray-900 dark:text-gray-100 tabular-nums">{rep[s.key] as number}</div>
                <div className="text-[10px] uppercase tracking-wide text-gray-400 dark:text-gray-500">{s.label}</div>
              </div>
            ))}
          </div>
          <div className="text-xs text-gray-400 dark:text-gray-500">Last seen {lastSeenLabel(rep.lastSeen)}</div>

          {/* Fundraisers this year */}
          <div>
            <h4 className="text-[11px] uppercase tracking-wide text-gray-400 dark:text-gray-500 font-semibold mb-2">Fundraisers this year</h4>
            {loading && <div className="text-sm text-gray-400 dark:text-gray-500">Loading…</div>}
            {error && <div className="text-sm text-rose-600 dark:text-rose-400">{error}</div>}
            {data && data.campaigns.length === 0 && <div className="text-sm text-gray-400 dark:text-gray-500">No fundraisers this year.</div>}
            {data && data.campaigns.length > 0 && (
              <div className="divide-y divide-gray-100 dark:divide-gray-700/60">
                {data.campaigns.map((c) => (
                  <div key={c.blastFundraiserId} className="py-2 flex items-center justify-between gap-3">
                    <div className="min-w-0">
                      <div className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">{c.organizationName ?? c.organizationId}</div>
                      <div className="text-[11px] text-gray-400 dark:text-gray-500">
                        {c.launchedAt ? new Date(c.launchedAt).toLocaleDateString() : '—'}{c.participationPct === null ? '' : ` · ${Math.round(c.participationPct)}% participation`}
                      </div>
                    </div>
                    <div className="text-sm font-semibold text-gray-900 dark:text-gray-100 tabular-nums flex-shrink-0">{formatDollarsCompact(c.raisedCents)}</div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </SlideOver>
  );
}
